import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ClipboardList, PackageCheck, RefreshCw, ScanLine } from 'lucide-react';
import PdaPage from '../components/PdaPage';
import {
  claimProductionTask,
  getMaterialRequestDetail,
  getOutboundOrderDetail,
  getProductionTaskDetail,
  getProductionTasks,
  listMaterialRequests,
  MaterialRequestDetail,
  MaterialRequestDetailItem,
  OutboundOrderDetail,
  OutboundOrderItemDetail,
  ProductionTaskDetail,
  ProductionTaskListItem,
  ProductionTaskStatus,
  TaskMaterialRequestSummary,
} from '../services/api';
import { navigateTo } from '../utils/navigation';
import { normalizeScannedCode } from '../utils/pdaCodes';
import { notifyError, notifyInfo } from '../utils/pdaFeedback';

type ViewMode = 'list' | 'detail';

const statusText: Record<ProductionTaskStatus, string> = {
  cancelled: '已取消',
  claimed: '已领取',
  done: '已完成',
  in_progress: '执行中',
  pending: '待领取',
};

const materialTypeText: Record<TaskMaterialRequestSummary['material_type'], string> = {
  mixed: '混合',
  offcut: '料头',
  raw_material: '原材料',
};

type MaterialDisplayItem = {
  id: string;
  type: TaskMaterialRequestSummary['material_type'];
  code: string;
  weight: number;
  locCode: string;
  batchNo: string;
  supplier: string;
  scanned?: boolean;
};

function formatDateTime(value?: string | null) {
  if (!value) {
    return '--';
  }

  return value.replace('T', ' ').replace(/\+.*$/, '').slice(0, 16);
}

function getTaskWeight(task: ProductionTaskListItem) {
  return getTaskMaterials(task).reduce((sum, material) => sum + (material.require_weight || 0), 0);
}

function getTaskMaterials(task?: Pick<ProductionTaskListItem, 'material_request' | 'material_requests'> | null) {
  if (!task) {
    return [];
  }

  if (Array.isArray(task.material_requests) && task.material_requests.length) {
    return task.material_requests.filter(Boolean);
  }

  return task.material_request ? [task.material_request] : [];
}

function formatWeight(value?: number | null) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  return numeric.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

function getMaterialRequestItems(detail: MaterialRequestDetail) {
  if (Array.isArray(detail.items) && detail.items.length) {
    return detail.items;
  }
  if (Array.isArray(detail.material_items) && detail.material_items.length) {
    return detail.material_items;
  }
  if (Array.isArray(detail.request_items) && detail.request_items.length) {
    return detail.request_items;
  }
  if (Array.isArray(detail.inventory_locks) && detail.inventory_locks.length) {
    return detail.inventory_locks;
  }
  return [];
}

function getMaterialItemType(item: MaterialRequestDetailItem, fallback: TaskMaterialRequestSummary['material_type']) {
  return (item.source || item.material_type || fallback) as TaskMaterialRequestSummary['material_type'];
}

function mapMaterialRequestItem(
  detail: MaterialRequestDetail,
  item: MaterialRequestDetailItem,
  index: number,
): MaterialDisplayItem {
  const type = getMaterialItemType(item, detail.material_type);
  const code = item.bid || item.ocsf || detail.material_desc || '--';
  const locCode = item.loc_code || item.location_code || item.current_loc || item.current_location || item.location || '-';
  const batchNo = item.batch_no || item.inbound_batch_no || item.inspection_no || '-';
  const supplier = item.supplier_name || item.supplier || '-';
  const weight = item.locked_weight ?? item.require_weight ?? item.stock_weight ?? item.avail_weight ?? item.free_weight ?? 0;

  return {
    batchNo,
    code,
    id: `${detail.id}-${item.id || item.inventory_id || item.offcut_stock_id || code}-${index}`,
    locCode,
    supplier,
    type,
    weight,
  };
}

function mapOutboundItem(order: OutboundOrderDetail, item: OutboundOrderItemDetail, index: number): MaterialDisplayItem {
  const code = item.bid || item.original_bid || item.sku || '--';

  return {
    batchNo: item.sku || '-',
    code,
    id: `outbound-${order.id}-${item.id || code}-${index}`,
    locCode: item.loc_code || '-',
    scanned: Boolean(item.scanned_at),
    supplier: item.scanned_at ? '已扫' : '-',
    type: /^LT-|^OCSF/i.test(code) ? 'offcut' : 'raw_material',
    weight: item.expected_weight ?? item.outbound_weight ?? 0,
  };
}

function getMaterialRequestCode(rawCode: string) {
  const code = rawCode.trim();
  const idMatch = code.match(/^(?:material-request|mr):(\d+)$/i);
  if (idMatch?.[1]) {
    return { id: idMatch[1], keyword: '' };
  }
  if (/^MR-[A-Za-z0-9-]+$/i.test(code)) {
    return { id: '', keyword: code };
  }
  return { id: '', keyword: '' };
}

function getOutboundOrderId(rawCode: string) {
  return rawCode.trim().match(/^outbound:(\d+)$/i)?.[1] || '';
}

function sortTasks(list: ProductionTaskListItem[]) {
  return [...list].sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bTime - aTime;
  });
}

function uniqueTasks(list: ProductionTaskListItem[]) {
  const map = new Map<number, ProductionTaskListItem>();
  list.forEach((task) => map.set(task.id, task));
  return sortTasks(Array.from(map.values()));
}

function MyTasks() {
  const scanTimerRef = useRef<number>(0);
  const [mode, setMode] = useState<ViewMode>('list');
  const [tasks, setTasks] = useState<ProductionTaskListItem[]>([]);
  const [selectedTask, setSelectedTask] = useState<ProductionTaskDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scanValue, setScanValue] = useState('');
  const [materialDetails, setMaterialDetails] = useState<MaterialRequestDetail[]>([]);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [scannedOutboundOrder, setScannedOutboundOrder] = useState<OutboundOrderDetail | null>(null);

  const grouped = useMemo(() => ({
    mine: tasks.filter((task) => task.status === 'claimed' || task.status === 'in_progress'),
    pending: tasks.filter((task) => task.status === 'pending'),
  }), [tasks]);

  async function loadTasks() {
    setLoading(true);
    try {
      const [mineResponse, pendingResponse] = await Promise.all([
        getProductionTasks({
          claimed_by_filter: 'me',
          limit: 100,
          page: 1,
          status_filter: 'all',
        }),
        getProductionTasks({
          limit: 100,
          page: 1,
          status_filter: 'pending',
        }),
      ]);
      setTasks(uniqueTasks([
        ...mineResponse.data.list.filter((task) => task.status === 'claimed' || task.status === 'in_progress'),
        ...pendingResponse.data.list,
      ]));
    } catch (error) {
      notifyError('任务列表加载失败');
    } finally {
      setLoading(false);
    }
  }

  async function openTask(task: ProductionTaskListItem) {
    setMode('detail');
    setDetailLoading(true);
    setScanValue('');
    setMaterialDetails([]);
    setScannedOutboundOrder(null);
    try {
      const response = await getProductionTaskDetail(task.id);
      const detail = response.data;
      setSelectedTask(detail);
      void loadMaterialDetails(getTaskMaterials(detail));
    } catch (error) {
      notifyError('任务详情加载失败');
      setSelectedTask(task as ProductionTaskDetail);
      void loadMaterialDetails(getTaskMaterials(task));
    } finally {
      setDetailLoading(false);
    }
  }

  async function loadMaterialDetails(materials: TaskMaterialRequestSummary[]) {
    if (!materials.length) {
      setMaterialDetails([]);
      return;
    }

    setMaterialLoading(true);
    try {
      const responses = await Promise.all(materials.map((material) => getMaterialRequestDetail(material.id)));
      setMaterialDetails(responses.map((response) => response.data));
    } catch (error) {
      notifyError('物料需求明细加载失败');
      setMaterialDetails([]);
    } finally {
      setMaterialLoading(false);
    }
  }

  async function handleClaim(task: ProductionTaskListItem | ProductionTaskDetail) {
    if (task.status !== 'pending') {
      void openTask(task);
      return;
    }

    setSubmitting(true);
    try {
      await claimProductionTask(task.id);
      notifyInfo('已领取任务');
      await loadTasks();
      await openTask({ ...task, status: 'claimed' });
    } catch (error: any) {
      notifyError(error?.msg || '领取任务失败');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleScanSubmit(rawValue?: string) {
    const code = normalizeScannedCode(rawValue ?? scanValue);
    if (!code) {
      notifyError('请先扫描二维码或 BID');
      return;
    }

    const outboundOrderId = getOutboundOrderId(code);
    if (outboundOrderId) {
      setMaterialLoading(true);
      try {
        const response = await getOutboundOrderDetail(outboundOrderId);
        setScannedOutboundOrder(response.data);
        setMaterialDetails([]);
        notifyInfo('已读取出库单物料明细');
        setScanValue('');
      } catch (error: any) {
        notifyError(error?.msg || '出库单读取失败');
      } finally {
        setMaterialLoading(false);
      }
      return;
    }

    const materialRequestCode = getMaterialRequestCode(code);
    if (materialRequestCode.id || materialRequestCode.keyword) {
      setMaterialLoading(true);
      try {
        let id = materialRequestCode.id;
        if (!id) {
          const listResponse = await listMaterialRequests({
            keyword: materialRequestCode.keyword,
            limit: 1,
            page: 1,
            status: 'all',
          });
          id = String(listResponse.data.list?.[0]?.id || '');
        }
        if (!id) {
          notifyError('未找到该物料需求单');
          return;
        }
        const response = await getMaterialRequestDetail(id);
        setMaterialDetails([response.data]);
        setScannedOutboundOrder(null);
        notifyInfo('已读取物料需求明细');
        setScanValue('');
      } catch (error: any) {
        notifyError(error?.msg || '物料需求读取失败');
      } finally {
        setMaterialLoading(false);
      }
      return;
    }

    notifyError('请先扫描物料需求 QR 或出库单 QR');
  }

  function handleScanChange(value: string) {
    setScanValue(value);
    window.clearTimeout(scanTimerRef.current);
    scanTimerRef.current = window.setTimeout(() => {
      if (value.trim().length >= 6) {
        void handleScanSubmit(value);
      }
    }, 180);
  }

  useEffect(() => {
    void loadTasks();
    return () => window.clearTimeout(scanTimerRef.current);
  }, []);

  function renderTaskCard(task: ProductionTaskListItem) {
    const materials = getTaskMaterials(task);
    const firstMaterial = materials[0];
    const actionText = task.status === 'pending' ? '领取' : '查看';

    return (
      <article className={`task-card task-card--${task.status}`} key={task.id}>
        <button className="task-card__main" onClick={() => void openTask(task)} type="button">
          <div className="task-card__head">
            <div className="task-card__title">
              <span className={`task-dot task-dot--${task.status}`} />
              {task.task_no}
            </div>
            <span className="task-type">{statusText[task.status]}</span>
          </div>
          <div className="task-card__meta">
            <ClipboardList aria-hidden="true" size={13} strokeWidth={2.2} />
            <span>{task.customer_order_no || task.plan_no || '无订单号'}</span>
            <span>计划量 {task.task_qty}</span>
          </div>
          <div className="task-card__time">
            {materials.length
              ? `${materials.length > 1 ? `${materials.length} 个需求` : `${materialTypeText[firstMaterial.material_type]}：${firstMaterial.material_desc}`} / ${formatWeight(materials.reduce((sum, item) => sum + (item.require_weight || 0), 0))}kg`
              : '暂无物料需求'}
          </div>
        </button>
        <button className="task-card__action" disabled={submitting} onClick={() => void handleClaim(task)} type="button">
          {actionText}
        </button>
      </article>
    );
  }

  function renderTaskGroup(title: string, list: ProductionTaskListItem[], status: 'pending' | 'in_progress') {
    if (!list.length) {
      return null;
    }

    return (
      <section className="task-section">
        <h2 className={`task-section__title task-section__title--${status}`}>{title}（{list.length}）</h2>
        {list.map(renderTaskCard)}
      </section>
    );
  }

  function renderList() {
    const totalWeight = tasks.reduce((sum, task) => sum + getTaskWeight(task), 0);

    return (
      <>
        <section className="task-overview">
          <div className="task-overview__title">任务概览</div>
          <div className="task-overview__stats">
            <div>
              <span className="task-overview__dot task-overview__dot--running" />
              <p>我的任务</p>
              <strong>{grouped.mine.length}</strong>
            </div>
            <div>
              <span className="task-overview__dot task-overview__dot--pending" />
              <p>待领取</p>
              <strong>{grouped.pending.length}</strong>
            </div>
            <div>
              <span className="task-overview__dot task-overview__dot--done" />
              <p>需求重量</p>
              <strong>{totalWeight}kg</strong>
            </div>
          </div>
        </section>

        {!loading && tasks.length === 0 ? (
          <section className="task-empty-card">
            <PackageCheck aria-hidden="true" size={64} strokeWidth={1.8} />
            <h2>暂无任务</h2>
            <p>当前没有待领取或正在执行的任务</p>
            <button className="task-primary-btn" onClick={() => void loadTasks()} type="button">刷新查看</button>
          </section>
        ) : null}

        {loading ? <section className="task-empty-card"><p>任务加载中...</p></section> : null}
        {renderTaskGroup('我的任务', grouped.mine, 'in_progress')}
        {renderTaskGroup('待领取', grouped.pending, 'pending')}
      </>
    );
  }

  function renderMaterialRequest(task: ProductionTaskDetail) {
    const materials = getTaskMaterials(task);
    const detailItems = materialDetails.reduce<MaterialDisplayItem[]>((list, detail) => {
      const nextItems = getMaterialRequestItems(detail).map((item, index) => mapMaterialRequestItem(detail, item, index));
      return list.concat(nextItems);
    }, []);
    const outboundItems = scannedOutboundOrder?.items?.map((item, index) => mapOutboundItem(scannedOutboundOrder, item, index)) || [];
    const displayItems = outboundItems.length ? outboundItems : detailItems;

    if (!materials.length && !scannedOutboundOrder) {
      return (
        <div className="task-material-empty">
          当前任务未关联物料需求
        </div>
      );
    }

    return (
      <>
        {materials.map((material) => (
          <div className="task-material-row" key={material.id}>
            <span>{materialTypeText[material.material_type]}</span>
            <strong>{material.material_desc}</strong>
            <small>{formatWeight(material.require_weight)}kg</small>
          </div>
        ))}
        {scannedOutboundOrder ? (
          <div className="task-material-source">
            出库单 {scannedOutboundOrder.order_no || scannedOutboundOrder.id}
            <small>{scannedOutboundOrder.scanned_pieces || 0}/{scannedOutboundOrder.total_pieces || outboundItems.length}</small>
          </div>
        ) : null}
        {materialLoading ? <div className="task-material-empty">物料明细加载中...</div> : null}
        {!materialLoading && displayItems.length ? (
          <div className="task-material-list">
            {displayItems.map((item) => (
              <div className={`task-material-item${item.scanned ? ' task-material-item--done' : ''}`} key={item.id}>
                <div className="task-material-item__main">
                  <div className="task-material-item__head">
                    <span>{materialTypeText[item.type]}</span>
                    <strong>{item.code}</strong>
                    <em>{formatWeight(item.weight)}kg</em>
                  </div>
                  <div className="task-material-item__meta">
                    <small>库位：{item.locCode}</small>
                    <small>批次：{item.batchNo}</small>
                    <small>供应商：{item.supplier}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </>
    );
  }

  function renderDetail() {
    if (!selectedTask) {
      return <section className="task-empty-card"><p>{detailLoading ? '任务详情加载中...' : '未选择任务'}</p></section>;
    }

    return (
      <>
        <section className="task-detail-card">
          <div className="task-detail-card__head">
            <div>
              <h1>{selectedTask.task_no}</h1>
              <span>{statusText[selectedTask.status]}</span>
            </div>
            <strong className={`task-detail-status task-detail-status--${selectedTask.status}`}>
              {selectedTask.material_request?.mr_no || '无需求单'}
            </strong>
          </div>
          <p>订单号：{selectedTask.customer_order_no || '--'} · 创建时间：{formatDateTime(selectedTask.created_at)}</p>
        </section>

        <section className="task-metrics">
          <div>
            <ClipboardList aria-hidden="true" size={22} strokeWidth={2.2} />
            <strong>{selectedTask.task_qty}</strong>
            <span>计划量</span>
          </div>
          <div>
            <Box aria-hidden="true" size={22} strokeWidth={2.2} />
            <strong>{selectedTask.actual_qty || 0}</strong>
            <span>已报工</span>
          </div>
          <div>
            <strong>{getTaskWeight(selectedTask)}kg</strong>
            <span>需求重量</span>
          </div>
        </section>

        <section className="task-panel">
          <h2>物料需求</h2>
          {renderMaterialRequest(selectedTask)}
        </section>

        <section className="task-panel">
          <h2>任务信息</h2>
          <div className="task-info-row"><span>排产单</span><strong>{selectedTask.plan_no || '--'}</strong></div>
          <div className="task-info-row"><span>客户</span><strong>{selectedTask.customer_name || '--'}</strong></div>
          <div className="task-info-row"><span>客户料号</span><strong>{selectedTask.customer_material_no || '--'}</strong></div>
          <div className="task-info-row"><span>物料描述</span><strong>{selectedTask.material_desc || '--'}</strong></div>
          <div className="task-info-row"><span>领取人</span><strong>{selectedTask.claimed_by_name || '--'}</strong></div>
          <div className="task-info-row"><span>备注</span><strong>{selectedTask.remark || '--'}</strong></div>
        </section>

        <section className="task-panel task-scan-panel">
          <h2>扫码领料</h2>
          <div className="task-scan-box">
            <ScanLine aria-hidden="true" size={19} strokeWidth={2.3} />
            <input
              autoComplete="off"
              inputMode="text"
              onChange={(event) => handleScanChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  void handleScanSubmit();
                }
              }}
              placeholder="扫描物料需求 QR"
              value={scanValue}
            />
          </div>
          <button className="task-primary-btn task-scan-btn" onClick={() => void handleScanSubmit()} type="button">扫码确认</button>
        </section>
      </>
    );
  }

  return (
    <PdaPage
      actionIcon={mode === 'list' ? <RefreshCw aria-hidden="true" size={13} strokeWidth={2.4} /> : undefined}
      actionLabel={mode === 'list' ? '刷新' : undefined}
      footer={mode === 'detail' && selectedTask ? (
        <div className="task-detail-footer">
          <button className="task-secondary-btn" onClick={() => setMode('list')} type="button">返回列表</button>
          <button
            className="task-primary-btn"
            disabled={submitting}
            onClick={() => {
              if (selectedTask.status === 'pending') {
                void handleClaim(selectedTask);
                return;
              }
              handleScanSubmit();
            }}
            type="button"
          >
            {selectedTask.status === 'pending' ? '领取任务' : '扫码领料'}
          </button>
        </div>
      ) : undefined}
      onAction={() => void loadTasks()}
      onBack={mode === 'detail' ? () => setMode('list') : () => navigateTo('/home')}
      pageClassName="my-tasks-page"
      title={mode === 'detail' ? '任务详情' : '我的任务'}
    >
      {mode === 'detail' ? renderDetail() : renderList()}
    </PdaPage>
  );
}

export default MyTasks;
